/**
 * Scales announcement text down so it stays on a single line instead of wrapping.
 * Opt in with [data-fit-text] on the section wrapper, [data-fit-min-size] sets the floor.
 */
(() => {
  const SECTION_SELECTOR = '[data-fit-text]';
  const TEXT_SELECTOR = '.announcement-bar__text';
  const DEFAULT_MIN_SIZE = 11;
  const STEP = 0.25;
  const MAX_STEPS = 32;

  const lastWidth = new WeakMap();

  const fitText = (element, minSize) => {
    element.style.fontSize = '';

    const available = element.clientWidth;
    if (!available) return;

    const baseSize = parseFloat(getComputedStyle(element).fontSize);
    if (!baseSize || element.scrollWidth <= available) return;

    let size = Math.max(minSize, (baseSize * available) / element.scrollWidth);
    element.style.fontSize = `${size.toFixed(2)}px`;

    // Letter spacing and word breaks make the first estimate optimistic, so trim it down.
    let steps = 0;
    while (element.scrollWidth > element.clientWidth && size > minSize && steps < MAX_STEPS) {
      size = Math.max(minSize, size - STEP);
      element.style.fontSize = `${size.toFixed(2)}px`;
      steps += 1;
    }
  };

  const fitSection = (section) => {
    const minSize = parseFloat(section.dataset.fitMinSize) || DEFAULT_MIN_SIZE;
    section.querySelectorAll(TEXT_SELECTOR).forEach((element) => fitText(element, minSize));
  };

  // Only react to width changes - font-size changes alter the section height,
  // which would otherwise feed straight back into the observer.
  const resizeObserver =
    'ResizeObserver' in window
      ? new ResizeObserver((entries) => {
          entries.forEach((entry) => {
            const width = entry.contentRect.width;
            if (lastWidth.get(entry.target) === width) return;
            lastWidth.set(entry.target, width);
            fitSection(entry.target);
          });
        })
      : null;

  const init = (root) => {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
      fitSection(section);
      if (resizeObserver) resizeObserver.observe(section);
    });
  };

  const start = () => {
    init(document);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => init(document));
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  if (!resizeObserver) {
    window.addEventListener('resize', () => init(document));
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
